/**
 * CSV export for the tax ledger (AC-8).
 *
 * There is no CSV utility anywhere in the webapp and no dependency for one —
 * verified: no papaparse, no file-saver, no xlsx. A `Blob` plus a synthetic
 * `<a download>` is ~20 lines and needs nothing, so this adds no dependency to
 * a bundle for a once-a-year export.
 *
 * Scope is the LEDGER TOTALS only. Bulk CSV of the settlement queue is ENG-5625,
 * where it ships beside bulk settlement and can share its selection model.
 */

/**
 * RFC-4180 quoting: wrap every field and double any embedded quote.
 *
 * Unconditional rather than only-when-needed, because the fields here include
 * operator-entered names and free text, and a "does this need quoting" test is
 * one missed character away from a broken row. A leading `=`/`+`/`-`/`@` is
 * prefixed with a quote so a spreadsheet reads it as text — an expert whose name
 * starts with one of those should not become a formula on open.
 */
function csvField(value: string | number): string {
  const raw = String(value);
  const guarded = /^[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return `"${guarded.replace(/"/g, '""')}"`;
}

export function toCsv(headers: string[], rows: (string | number)[][]): string {
  // CRLF and a trailing newline: RFC-4180, and Excel is happier for it.
  return [headers, ...rows].map((row) => row.map(csvField).join(',')).join('\r\n') + '\r\n';
}

/**
 * Hand the browser a file.
 *
 * The object URL is revoked on the next tick rather than immediately — Safari
 * cancels an in-flight download when its blob URL is revoked synchronously.
 */
export function downloadCsv(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
